/**
 * The gear map (Settings and the gear, Part 1 #5, canvas v25 board "3 · The gear"): every
 * surface whose page head carries the settings gear, where its gear goes, and its name.
 *
 * A surface passes its entry to `HubPage settings={GEAR.projects}` (or `HubSettingsGear`
 * for a head it draws itself). The settings page reads the same map to say "Back to
 * Projects" when someone arrives from a gear. Who sees a gear is the section's own gate
 * (question 1 A): the first target the person may open wins, a personal one last
 * (question 2 A); with none, no gear.
 *
 * Surfaces whose company-wide settings still live on their own page (L10 meeting settings,
 * Forecasting, Time Reports, the weekly review) keep pointing there until
 * each moves into Settings in its own ticket (question 9 A); they carry no entry here.
 */
import type { HubSettingsTarget } from '../hub/HubSettingsGear';

export interface GearSurface {
  /** The surface's name, for the tooltip and "Back to …". */
  label: string;
  /** The paths it lives at (a prefix match). */
  paths: string[];
  /** Where its gear goes; the first one the person may open wins. */
  targets: HubSettingsTarget[];
}

export const GEAR = {
  email: { label: 'Email', paths: ['/email', '/inbox'], targets: [{ section: 'inbox' }, { section: 'profile', panel: 'email-filters', personal: true }] },
  helpdesk: { label: 'Help Desk', paths: ['/helpdesk'], targets: [{ section: 'inbox' }, { section: 'profile', panel: 'email-filters', personal: true }] },
  calendar: { label: 'Calendar', paths: ['/scheduling/calendar'], targets: [{ section: 'connections', panel: 'google-calendar', personal: true }] },
  todos: { label: 'To-dos', paths: ['/todos'], targets: [{ section: 'notifications', panel: 'todos', personal: true }] },
  contacts: { label: 'Contacts', paths: ['/contacts'], targets: [{ section: 'contacts' }] },
  timeTracker: { label: 'Time Tracker', paths: ['/time-tracking'], targets: [{ section: 'timeTracking' }] },
  tickets: { label: 'Support Tickets', paths: ['/operations/tickets'], targets: [{ section: 'support' }] },
  projects: { label: 'Projects', paths: ['/operations/projects'], targets: [{ section: 'projects', panel: 'stage-playbook' }] },
  techAudit: { label: 'Tech audit', paths: [], targets: [{ section: 'techAudit' }] },
  deals: { label: 'Deals', paths: ['/sales/pipelines', '/sales/deals', '/sales'], targets: [{ section: 'sales', panel: 'stage-playbook' }] },
  campaigns: { label: 'Campaigns', paths: ['/sales/campaigns'], targets: [{ section: 'campaigns' }] },
  callCrusher: { label: 'Call Crusher', paths: ['/sales/campaigns/call-crusher'], targets: [{ section: 'profile', panel: 'calling-hours', personal: true }] },
  comms: { label: 'Comms', paths: ['/comms'], targets: [{ section: 'communications' }, { section: 'profile', panel: 'your-lines', personal: true }] },
  forms: { label: 'Forms', paths: ['/forms'], targets: [{ section: 'forms' }] },
  bookings: { label: 'Bookings', paths: ['/scheduling'], targets: [{ section: 'communications', panel: 'booking-texts' }] },
  kickoff: { label: 'Weekly Kickoff', paths: ['/eos/kickoff'], targets: [{ section: 'meetings' }] },
  quarterly: { label: 'Onsites', paths: ['/eos/quarterly'], targets: [{ section: 'ai', panel: 'ai-staff' }] },
  scorecard: { label: 'Scorecard', paths: ['/analytics/scorecard', '/reports/scorecards'], targets: [{ section: 'quarterly' }] },
  myScorecard: { label: 'My scorecard', paths: ['/my-scorecard'], targets: [{ section: 'quarterly' }] },
  finances: { label: 'Finances', paths: ['/analytics/finances'], targets: [{ section: 'ai', panel: 'ai-staff' }, { section: 'integrations' }] },
  dataCenter: { label: 'Data Center', paths: ['/analytics/data-center'], targets: [{ section: 'integrations' }] },
  notifications: { label: 'Notifications', paths: [], targets: [{ section: 'notifications', personal: true }] },
  lotn: { label: 'Lord of the Notes', paths: ['/call-intelligence'], targets: [{ section: 'calls' }] },
} satisfies Record<string, GearSurface>;

export type GearSurfaceKey = keyof typeof GEAR;

/** The surface a path belongs to (the longest matching path wins), for "Back to …". */
export function surfaceLabelForPath(path: string): string {
  const clean = path.split(/[?#]/)[0];
  let best: { label: string; len: number } | null = null;
  for (const g of Object.values(GEAR) as GearSurface[]) {
    for (const p of g.paths) {
      if ((clean === p || clean.startsWith(`${p}/`)) && (!best || p.length > best.len)) best = { label: g.label, len: p.length };
    }
  }
  return best?.label ?? 'where you were';
}
